import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import AnonymizeModal from "./AnonymizeModal";
import { BulkAssignBar, DemographicCell, SubgroupSummary } from "./SubgroupAssign";

const STATUS_STYLES = {
  not_started: { label: "Not started", bg: "#EEEEEE", color: "#555555" },
  in_progress: { label: "In progress", bg: "#D8E9EF", color: "#2F6B7C" },
  completed:   { label: "Completed",   bg: "#E4EFE0", color: "#3D6B2D" },
};

function statusOf(r) {
  if (r.completed_at) return "completed";
  if (r.started_at) return "in_progress";
  return "not_started";
}

function surveyUrlFor(token) {
  return `${window.location.origin}/survey/${token}`;
}

function timeAgo(isoString) {
  if (!isoString) return "";
  const then = new Date(isoString).getTime();
  if (isNaN(then)) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
}

function parseCsv(text, demographicKeys, expectedSet) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) {
    return { error: "Need at least one header row and one data row." };
  }
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  // email is required; the rest must match demographic keys exactly (order-independent).
  if (!headers.includes("email")) {
    return { error: "First column header must be 'email'." };
  }
  const csvDemoKeys = headers.filter((h) => h !== "email");
  const csvSet = new Set(csvDemoKeys);
  const expected = new Set(demographicKeys);
  const missing = [...expected].filter((k) => !csvSet.has(k));
  const extra = [...csvSet].filter((k) => !expected.has(k));
  if (missing.length || extra.length) {
    const parts = [];
    if (missing.length) parts.push(`missing: ${missing.join(", ")}`);
    if (extra.length) parts.push(`unexpected: ${extra.join(", ")}`);
    return {
      error: `CSV headers don't match deployment demographics (${parts.join(" · ")}). Expected: email, ${demographicKeys.join(", ")}.`,
    };
  }
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(",").map((c) => c.trim());
    if (cells.length !== headers.length) {
      return { error: `Row ${i + 1} has ${cells.length} columns; expected ${headers.length}.` };
    }
    const row = { email: null, demographics: {} };
    headers.forEach((h, idx) => {
      if (h === "email") row.email = cells[idx] || null;
      else row.demographics[h] = cells[idx];
    });
    // Validate demographic values against the deployment's allowed values.
    for (const [k, v] of Object.entries(row.demographics)) {
      if (!expectedSet[k]?.includes(v)) {
        return {
          error: `Row ${i + 1}: '${v}' is not a valid value for '${k}'. Allowed: ${expectedSet[k].join(", ")}.`,
        };
      }
    }
    rows.push(row);
  }
  return { rows };
}

export default function Respondents({ deploymentId, demographicConfig, status, onEditDemographics, deploymentMode }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const isOri = deploymentMode !== "individual";
  const canDownloadReport = isOri && (user?.role === "super_admin" || user?.role === "consultant");
  const [downloadingRespondentId, setDownloadingRespondentId] = useState(null);
  const [downloadingBulk, setDownloadingBulk] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [anonymizeTarget, setAnonymizeTarget] = useState(null);  // respondent obj
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addMode, setAddMode] = useState(null);  // null | "single" | "csv"
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const [addSuccess, setAddSuccess] = useState("");
  const [singleEmail, setSingleEmail] = useState("");
  const [singleDemo, setSingleDemo] = useState({});
  const [csvText, setCsvText] = useState("");
  const [copiedToken, setCopiedToken] = useState(null);

  const demographicKeys = Object.keys(demographicConfig || {});
  const canAssign = isOri && demographicKeys.length > 0;
  const [selected, setSelected] = useState(() => new Set());
  const [savingId, setSavingId] = useState(null);
  const [bulkKey, setBulkKey] = useState("");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [assignMsg, setAssignMsg] = useState(null);  // {ok, text}
  const activeBulkKey = bulkKey || demographicKeys[0] || "";

  function toggleSelected(id) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function assignOne(respondent, key, value) {
    setSavingId(respondent.id);
    setAssignMsg(null);
    try {
      const { data } = await api.patch(
        `/api/deployments/${deploymentId}/respondents/${respondent.id}/demographics`,
        { demographics: { [key]: value } },
      );
      setRows((rs) => rs.map((r) => (r.id === respondent.id ? { ...r, demographics: data.respondent.demographics } : r)));
    } catch (e) {
      setAssignMsg({ ok: false, text: formatApiError(e) });
    } finally {
      setSavingId(null);
    }
  }

  async function applyBulk() {
    setBulkBusy(true);
    setAssignMsg(null);
    try {
      const { data } = await api.post(`/api/deployments/${deploymentId}/respondents/demographics/bulk`, {
        respondent_ids: [...selected],
        key: activeBulkKey,
        value: bulkValue === "__clear__" ? null : bulkValue,
      });
      const skipped = data.skipped_anonymized ? ` · ${data.skipped_anonymized} anonymized skipped` : "";
      const same = data.unchanged ? ` · ${data.unchanged} already in that group` : "";
      setAssignMsg({ ok: true, text: `${data.updated} respondent${data.updated === 1 ? "" : "s"} updated${same}${skipped}.` });
      setSelected(new Set());
      setBulkValue("");
      await load();
    } catch (e) {
      setAssignMsg({ ok: false, text: formatApiError(e) });
    } finally {
      setBulkBusy(false);
    }
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/deployments/${deploymentId}/respondents`);
      setRows(data.respondents || []);
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  // Data-fetching effect — setState lives inside the async IIFE so it
  // doesn't fire synchronously in the effect body. The eslint rule
  // react-hooks/set-state-in-effect is OFF in package.json anyway, but
  // this shape keeps the linter overlay happy too.
  useEffect(() => {
    (async () => { await load(); })();
  }, [deploymentId]);

  // Sprint 9 — email send buttons are visible iff RESEND_API_KEY is set
  // on the deployed backend. Preview/local without the key gets a
  // muted explanation rather than buttons that 503.
  const [emailConfigured, setEmailConfigured] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/email/status");
        setEmailConfigured(Boolean(data?.configured));
      } catch {
        setEmailConfigured(false);
      }
    })();
  }, []);

  const [sending, setSending] = useState(null);    // 'invite' | 'remind' | null
  const [sendSummary, setSendSummary] = useState(null);  // {kind, counts}

  async function sendBatch(kind) {
    const path = kind === "invite" ? "send-invitations" : "send-reminders";
    setSending(kind);
    setSendSummary(null);
    try {
      const { data } = await api.post(`/api/deployments/${deploymentId}/${path}`, {
        respondent_ids: [],
      });
      setSendSummary({ kind, counts: data.summary || {} });
      await load();
    } catch (e) {
      setSendSummary({ kind, error: formatApiError(e) });
    } finally {
      setSending(null);
    }
  }

  // For the header counters: how many haven't been invited, how many
  // are eligible for a reminder. The send-now / send-reminders buttons
  // disable when their respective counter hits zero.
  const uninvited = rows.filter((r) => r.email && !r.invited_at).length;
  const remindable = rows.filter(
    (r) => r.email && r.invited_at && !r.completed_at,
  ).length;
  const completedDownloadable = rows.filter(
    (r) => r.completed_at && !r.anonymized_at,
  ).length;

  function resetForms() {
    setSingleEmail("");
    setSingleDemo({});
    setCsvText("");
    setAddError("");
  }

  async function submitSingle(e) {
    e.preventDefault();
    setAddError("");
    setAddSuccess("");
    // Validate every demographic key is filled.
    for (const k of demographicKeys) {
      if (!singleDemo[k]) {
        setAddError(`Demographic '${k}' is required.`);
        return;
      }
    }
    try {
      setAdding(true);
      await api.post(`/api/deployments/${deploymentId}/invitations`, {
        rows: [{ email: singleEmail.trim() || null, demographics: singleDemo }],
      });
      setAddSuccess("1 respondent added.");
      resetForms();
      setAddMode(null);
      await load();
    } catch (e) {
      setAddError(formatApiError(e));
    } finally {
      setAdding(false);
    }
  }

  async function submitCsv(e) {
    e.preventDefault();
    setAddError("");
    setAddSuccess("");
    const parsed = parseCsv(csvText, demographicKeys, demographicConfig || {});
    if (parsed.error) {
      setAddError(parsed.error);
      return;
    }
    try {
      setAdding(true);
      await api.post(`/api/deployments/${deploymentId}/invitations`, {
        rows: parsed.rows,
      });
      setAddSuccess(`${parsed.rows.length} respondents added.`);
      resetForms();
      setAddMode(null);
      await load();
    } catch (e) {
      setAddError(formatApiError(e));
    } finally {
      setAdding(false);
    }
  }

  async function copyUrl(token) {
    try {
      await navigator.clipboard.writeText(surveyUrlFor(token));
      setCopiedToken(token);
      setTimeout(() => setCopiedToken((t) => (t === token ? null : t)), 1600);
    } catch {
      // clipboard API may be blocked; nothing to do — the button still works on subsequent clicks
    }
  }

  async function downloadReport(respondent) {
    setDownloadingRespondentId(respondent.id);
    setDownloadError("");
    try {
      const res = await api.get(
        `/api/deployments/${deploymentId}/respondents/${respondent.id}/coaching-report/download`,
        { responseType: "blob" },
      );
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m ? m[1] : `coaching-report-${respondent.id}.pdf`;
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError(formatApiError(e));
    } finally {
      setDownloadingRespondentId(null);
    }
  }

  async function downloadAllReports() {
    setDownloadingBulk(true);
    setDownloadError("");
    try {
      const res = await api.get(
        `/api/deployments/${deploymentId}/coaching-reports/zip`,
        { responseType: "blob" },
      );
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m ? m[1] : "cohort-reports.zip";
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError(formatApiError(e));
    } finally {
      setDownloadingBulk(false);
    }
  }

  const surveyOpenable = status === "open";

  return (
    <div className="space-y-8" data-testid="respondents-tab">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-1">Respondents</div>
          <h2 style={{ fontSize: "1.5rem" }}>
            {rows.length} {rows.length === 1 ? "respondent" : "respondents"}
          </h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {canDownloadReport && (
            <button
              onClick={downloadAllReports}
              disabled={downloadingBulk || completedDownloadable === 0}
              data-testid="download-all-reports-button"
              title={
                completedDownloadable === 0
                  ? "No completed respondents to bundle yet"
                  : `Download a .zip with one coaching-report PDF per completed respondent (${completedDownloadable} PDF${completedDownloadable === 1 ? "" : "s"}). Each download is logged in the admin audit trail.`
              }
              style={{
                fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                fontSize: "11px",
                color: completedDownloadable === 0 || downloadingBulk ? "#6F6F6F" : "#2F6B7C",
                background: "transparent",
                border: `1px solid ${completedDownloadable === 0 || downloadingBulk ? "#DDDDDD" : "#4592A8"}`,
                padding: "10px 16px",
                cursor: completedDownloadable === 0 || downloadingBulk ? "not-allowed" : "pointer",
              }}
            >
              {downloadingBulk
                ? "Bundling…"
                : `Download all reports${completedDownloadable > 0 ? ` (${completedDownloadable})` : ""}`}
            </button>
          )}
          {emailConfigured && (
            <>
              <button
                onClick={() => sendBatch("invite")}
                disabled={sending !== null || uninvited === 0}
                data-testid="send-invitations-button"
                title={uninvited === 0 ? "Everyone with an email has been invited" : `Send invitation email to ${uninvited} respondent${uninvited === 1 ? "" : "s"}`}
                style={{
                  fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                  fontSize: "11px",
                  color: uninvited === 0 || sending ? "#6F6F6F" : "#2F6B7C",
                  background: "transparent",
                  border: `1px solid ${uninvited === 0 || sending ? "#DDDDDD" : "#2F6B7C"}`,
                  padding: "10px 16px",
                  cursor: uninvited === 0 || sending ? "not-allowed" : "pointer",
                }}
              >
                {sending === "invite" ? "Sending…" : `Send invitations${uninvited > 0 ? ` (${uninvited})` : ""}`}
              </button>
              <button
                onClick={() => sendBatch("remind")}
                disabled={sending !== null || remindable === 0}
                data-testid="send-reminders-button"
                title={remindable === 0 ? "No respondents are eligible for a reminder right now" : `Send reminder email to ${remindable} in-progress / not-yet-started respondent${remindable === 1 ? "" : "s"}`}
                style={{
                  fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                  fontSize: "11px",
                  color: remindable === 0 || sending ? "#6F6F6F" : "#2F6B7C",
                  background: "transparent",
                  border: `1px solid ${remindable === 0 || sending ? "#DDDDDD" : "#2F6B7C"}`,
                  padding: "10px 16px",
                  cursor: remindable === 0 || sending ? "not-allowed" : "pointer",
                }}
              >
                {sending === "remind" ? "Sending…" : `Send reminders${remindable > 0 ? ` (${remindable})` : ""}`}
              </button>
            </>
          )}
          <button
            onClick={() => {
              resetForms();
              setAddMode(addMode ? null : "single");
            }}
            data-testid="add-respondents-button"
            style={{
              fontFamily: "Oswald",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontSize: "11px",
              color: "#FBFAF7",
              background: "#2F6B7C",
              border: 0,
              padding: "10px 16px",
              cursor: "pointer",
            }}
          >
            {addMode ? "Close" : "+ Add respondents"}
          </button>
          {onEditDemographics && (
            <button
              onClick={onEditDemographics}
              data-testid="edit-demographics-button"
              title="Add or rename roles, teams, or other demographics. Existing respondents keep their values."
              style={{
                fontFamily: "Oswald",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontSize: "11px",
                color: "#1A1A1A",
                background: "transparent",
                border: "1px solid #DDDDDD",
                padding: "10px 16px",
                cursor: "pointer",
              }}
            >
              Manage demographics
            </button>
          )}
        </div>
      </div>
      {sendSummary && (
        <div
          className="border-l-4 pl-4 py-3 text-sm"
          style={{
            borderColor: sendSummary.error || (sendSummary.counts?.failed > 0) ? "#AD393E" : "#2F6B7C",
            background: "#FAF7F1",
          }}
          data-testid="send-summary"
        >
          {sendSummary.error ? (
            <span style={{ color: "#AD393E" }}>Couldn&rsquo;t send: {sendSummary.error}</span>
          ) : (
            <span>
              <strong>{sendSummary.kind === "invite" ? "Invitations" : "Reminders"}:</strong>{" "}
              {sendSummary.counts.sent || 0} sent
              {sendSummary.counts.failed > 0 && (
                <span style={{ color: "#AD393E" }}> &middot; {sendSummary.counts.failed} failed</span>
              )}
              {sendSummary.counts.skipped > 0 && (
                <span style={{ color: "#6F6F6F" }}> &middot; {sendSummary.counts.skipped} skipped</span>
              )}
              {sendSummary.counts.no_email > 0 && (
                <span style={{ color: "#6F6F6F" }}> &middot; {sendSummary.counts.no_email} without email</span>
              )}
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="panel" style={{ borderColor: "#AD393E" }} data-testid="respondents-error">
          <div className="eyebrow mb-1" style={{ color: "#AD393E" }}>Couldn&rsquo;t load</div>
          <div className="text-sm">{error}</div>
        </div>
      )}

      {downloadError && (
        <div className="panel" style={{ borderColor: "#AD393E" }} data-testid="download-report-error">
          <div className="eyebrow mb-1" style={{ color: "#AD393E" }}>Couldn&rsquo;t download report</div>
          <div className="text-sm">{downloadError}</div>
        </div>
      )}

      {addSuccess && (
        <div className="panel" style={{ borderColor: "#3D6B2D" }} data-testid="add-success">
          <div className="text-sm">{addSuccess}</div>
        </div>
      )}

      {addMode && (
        <div className="panel" data-testid="add-respondents-panel">
          <div className="flex items-center gap-1 border-b border-gray-100 mb-6" role="tablist">
            {[["single", "Single"], ["csv", "CSV paste"]].map(([key, label]) => {
              const active = addMode === key;
              return (
                <button
                  key={key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => { setAddMode(key); setAddError(""); }}
                  className="px-4 py-2 text-sm"
                  style={{
                    fontFamily: "Oswald",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: active ? "#AD393E" : "#555555",
                    borderBottom: active ? "2px solid #AD393E" : "2px solid transparent",
                    marginBottom: "-1px",
                  }}
                  data-testid={`add-tab-${key}`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {addError && (
            <div className="mb-4 text-sm" style={{ color: "#AD393E" }} data-testid="add-error">
              {addError}
            </div>
          )}

          {addMode === "single" && (
            <form onSubmit={submitSingle} className="space-y-4" data-testid="single-form">
              <div>
                <label className="label">Email <span className="text-gray-500 text-xs">(optional)</span></label>
                <input
                  type="email"
                  value={singleEmail}
                  onChange={(e) => setSingleEmail(e.target.value)}
                  placeholder="name@example.com"
                  data-testid="single-email"
                  style={{
                    width: "100%", maxWidth: 360, padding: "10px 12px",
                    border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                  }}
                />
              </div>
              {demographicKeys.map((k) => (
                <div key={k}>
                  <label className="label">{k}</label>
                  <div className="flex items-center gap-3 flex-wrap">
                    <select
                      value={singleDemo[k] || ""}
                      onChange={(e) => setSingleDemo((s) => ({ ...s, [k]: e.target.value }))}
                      data-testid={`single-demo-${k}`}
                      aria-label={`Demographic ${k}`}
                      style={{
                        maxWidth: 220, padding: "10px 12px",
                        border: "1px solid #E0E0E0", background: "white", fontSize: "14px",
                      }}
                    >
                      <option value="">Select…</option>
                      {demographicConfig[k].map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                    {onEditDemographics && (
                      <button
                        type="button"
                        onClick={onEditDemographics}
                        className="btn-quiet text-xs"
                        data-testid={`add-${k}-value-link`}
                        title={`Add a new value to '${k}' without leaving this screen`}
                      >
                        + Add new {k}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={adding}
                  data-testid="single-submit"
                  style={{
                    fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                    fontSize: "11px", color: "#FBFAF7", background: "#AD393E",
                    border: 0, padding: "10px 18px", cursor: adding ? "wait" : "pointer",
                  }}
                >
                  {adding ? "Adding…" : "Add respondent"}
                </button>
              </div>
            </form>
          )}

          {addMode === "csv" && (
            <form onSubmit={submitCsv} className="space-y-4" data-testid="csv-form">
              <div>
                <label className="label">Paste CSV</label>
                <p className="text-xs text-gray-500 mb-2">
                  First row: <code>email,{demographicKeys.join(",")}</code>. Email column may be blank for
                  anonymous respondents. Demographic values must match the deployment&rsquo;s configured options.
                </p>
                <textarea
                  rows={8}
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                  placeholder={`email,${demographicKeys.join(",")}\nalice@example.com,${demographicKeys.map((k) => demographicConfig[k][0] || "").join(",")}\n,${demographicKeys.map((k) => demographicConfig[k][0] || "").join(",")}`}
                  data-testid="csv-textarea"
                  style={{
                    width: "100%", padding: "12px", fontFamily: "ui-monospace, monospace",
                    fontSize: "12px", border: "1px solid #E0E0E0", background: "white",
                    minHeight: "200px",
                  }}
                />
              </div>
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={adding}
                  data-testid="csv-submit"
                  style={{
                    fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                    fontSize: "11px", color: "#FBFAF7", background: "#AD393E",
                    border: 0, padding: "10px 18px", cursor: adding ? "wait" : "pointer",
                  }}
                >
                  {adding ? "Adding…" : "Add respondents"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {!loading && rows.length === 0 && !addMode && (
        <div className="panel" data-testid="respondents-empty">
          <div className="eyebrow mb-2">No respondents yet</div>
          <p className="text-sm text-gray-700">
            Add respondents to generate survey URLs. Each respondent gets a unique tokenized URL the
            consultant can share via their own email client.
          </p>
        </div>
      )}

      {canAssign && <SubgroupSummary rows={rows} demographicConfig={demographicConfig} />}

      {assignMsg && (
        <div
          className="border-l-4 pl-4 py-2 text-sm"
          style={{ borderColor: assignMsg.ok ? "#3D6B2D" : "#AD393E", background: "#FAF7F1", color: assignMsg.ok ? undefined : "#AD393E" }}
          data-testid="assign-message"
        >
          {assignMsg.text}
        </div>
      )}

      {rows.length > 0 && (
        <div className="panel" style={{ padding: 0, overflowX: "auto" }}>
          {canAssign && selected.size > 0 && (
            <BulkAssignBar
              count={selected.size}
              demographicConfig={demographicConfig}
              bulkKey={activeBulkKey}
              bulkValue={bulkValue}
              onKey={(k) => { setBulkKey(k); setBulkValue(""); }}
              onValue={setBulkValue}
              onApply={applyBulk}
              onClear={() => setSelected(new Set())}
              busy={bulkBusy}
            />
          )}
          <table className="w-full" data-testid="respondents-table">
            <thead>
              <tr style={{ borderBottom: "1px solid #E8E8E8" }}>
                {canAssign && (
                  <th className="p-4" style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      aria-label="Select all respondents"
                      data-testid="select-all-respondents"
                      checked={rows.some((r) => !r.anonymized_at) && rows.filter((r) => !r.anonymized_at).every((r) => selected.has(r.id))}
                      onChange={(e) => setSelected(e.target.checked ? new Set(rows.filter((r) => !r.anonymized_at).map((r) => r.id)) : new Set())}
                    />
                  </th>
                )}
                {["Email", canAssign ? "Subgroups" : "Demographics", "Status", "Survey URL"].map((h, i) => (
                  <th
                    key={h}
                    className="text-xs text-gray-500 p-4"
                    style={{
                      fontFamily: "Oswald",
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      fontWeight: 600,
                      textAlign: i === 3 ? "right" : "left",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = statusOf(r);
                const style = STATUS_STYLES[s];
                return (
                  <tr
                    key={r.id}
                    style={{ borderBottom: "1px solid #F4F1EB", background: selected.has(r.id) ? "#FAF7F1" : undefined }}
                    data-testid={`respondent-${r.id}`}
                  >
                    {canAssign && (
                      <td className="p-4">
                        <input
                          type="checkbox"
                          aria-label={`Select ${r.email || "respondent"}`}
                          data-testid={`select-respondent-${r.id}`}
                          disabled={!!r.anonymized_at}
                          checked={selected.has(r.id)}
                          onChange={() => toggleSelected(r.id)}
                        />
                      </td>
                    )}
                    <td className="p-4 text-sm">{r.email || <span className="text-gray-500">—</span>}</td>
                    <td className="p-4 text-sm" style={{ fontFamily: "Oswald", letterSpacing: "0.06em" }}>
                      <DemographicCell
                        respondent={r}
                        demographicKeys={demographicKeys}
                        demographicConfig={demographicConfig || {}}
                        editable={canAssign && !r.anonymized_at}
                        saving={savingId === r.id}
                        onChange={assignOne}
                      />
                    </td>
                    <td className="p-4">
                      <span
                        className="text-xs px-3 py-1"
                        style={{
                          fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase",
                          background: style.bg, color: style.color,
                        }}
                      >
                        {style.label}
                      </span>
                      {(r.invited_at || r.last_reminded_at) && (
                        <div
                          className="text-xs text-gray-600 mt-1"
                          style={{ fontFamily: "Oswald", letterSpacing: "0.06em" }}
                          data-testid={`send-state-${r.id}`}
                          title={r.last_send_error ? `Last error: ${r.last_send_error}` : undefined}
                        >
                          {r.invited_at && <>Invited {timeAgo(r.invited_at)}</>}
                          {r.last_reminded_at && <> · Reminded {timeAgo(r.last_reminded_at)}</>}
                          {r.last_send_error && <span style={{ color: "#AD393E" }}> · ⚠ send error</span>}
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      {!surveyOpenable && (
                        <span className="text-xs text-gray-500 mr-2">
                          ({status} — link won&rsquo;t accept responses)
                        </span>
                      )}
                      <button
                        onClick={() => copyUrl(r.token)}
                        data-testid={`copy-url-${r.id}`}
                        style={{
                          fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase",
                          fontSize: "10px",
                          color: copiedToken === r.token ? "#3D6B2D" : "#2F6B7C",
                          background: "transparent",
                          border: `1px solid ${copiedToken === r.token ? "#3D6B2D" : "#2F6B7C"}`,
                          padding: "6px 12px",
                          cursor: "pointer",
                        }}
                      >
                        {copiedToken === r.token ? "Copied ✓" : "Copy link"}
                      </button>
                      {canDownloadReport && s === "completed" && !r.anonymized_at && (
                        <button
                          onClick={() => downloadReport(r)}
                          disabled={downloadingRespondentId === r.id}
                          data-testid={`download-report-${r.id}`}
                          title="Download this cohort member's individual coaching report. Every download is logged in the admin audit trail."
                          style={{
                            marginLeft: 8,
                            fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase",
                            fontSize: "10px",
                            color: downloadingRespondentId === r.id ? "#999898" : "#2F6B7C",
                            background: "transparent",
                            border: `1px solid ${downloadingRespondentId === r.id ? "#D0D0D0" : "#4592A8"}`,
                            padding: "6px 12px",
                            cursor: downloadingRespondentId === r.id ? "wait" : "pointer",
                          }}
                        >
                          {downloadingRespondentId === r.id ? "Preparing…" : "Download report"}
                        </button>
                      )}
                      {isSuperAdmin && !r.anonymized_at && (
                        <button
                          onClick={() => setAnonymizeTarget(r)}
                          data-testid={`anonymize-${r.id}`}
                          title="Anonymize this respondent's data under GDPR Art. 17. Demographics are cleared, email is dropped, free-text becomes [anonymized]. Item scores stay so the aggregate math is unaffected."
                          style={{
                            marginLeft: 8,
                            fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase",
                            fontSize: "10px",
                            color: "#AD393E",
                            background: "transparent",
                            border: "1px solid #AD393E",
                            padding: "6px 12px",
                            cursor: "pointer",
                          }}
                        >
                          Anonymize
                        </button>
                      )}
                      {r.anonymized_at && (
                        <span
                          className="text-xs ml-2"
                          style={{ fontFamily: "Oswald", letterSpacing: "0.1em", color: "#6F6F6F" }}
                          data-testid={`anonymized-${r.id}`}
                        >
                          ANONYMIZED {timeAgo(r.anonymized_at)}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {anonymizeTarget && (
        <AnonymizeModal
          respondent={anonymizeTarget}
          onClose={() => setAnonymizeTarget(null)}
          onSuccess={async () => {
            setAnonymizeTarget(null);
            await load();
          }}
        />
      )}
    </div>
  );
}
